import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, Landmark, FileCheck2, Lock, ListChecks, Ban, Loader2 } from "lucide-react";
import { useAuth, startLogin } from "@/context/AuthContext";
import { PartnerMark } from "@/components/PublicLayout";

export const STEPS = [
  ["Tell us about your business or invention", "One reusable profile for every application, plus a project record for each innovation."],
  ["Upload supporting documents", "Encrypted before storage. Facts found in documents are labelled and only used once you confirm them."],
  ["Receive an IP action plan and funding matches", "Rule-based guidance on patents, trade marks, designs, copyright records and know-how, plus eligibility checks against published criteria."],
  ["Complete missing information", "A guided series of questions fills the gaps. Optional AI suggestions are always labelled and need your review."],
  ["Review documents, costs and declarations", "Completeness checks, budget calculator, evidence links and a final review screen."],
  ["Approve or export an application pack", "Approve the exact version. Where no verified submission route exists, export a complete pack with an official-portal handoff."],
  ["Track progress and deadlines", "Statuses, official references, receipts, further-information requests, renewals and reporting dates."],
];

const PREVIEW_ROUTES = [["Trade mark", "Recommended", "bg-teal-50 text-teal-800"], ["Patent", "Professional review", "bg-blue-50 text-blue-800"], ["Know-how", "Recommended", "bg-teal-50 text-teal-800"]];

const EASE = [0.2, 0.7, 0.2, 1];
const rise = (d = 0) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: d, ease: EASE } });

const useTick = (still) => {
  const [n, setN] = useState(still ? 99 : 0);
  useEffect(() => {
    if (still) return undefined;
    const t = setInterval(() => setN((x) => x + 1), 650);
    return () => clearInterval(t);
  }, [still]);
  return n;
};

const Preview = () => {
  const still = useReducedMotion();
  const n = useTick(still);
  const shown = Math.min(n, PREVIEW_ROUTES.length + 1);
  const steps = Math.min(4, Math.max(0, n - 2));
  return (
    <div className="relative mx-auto max-w-md lg:mt-4" data-testid="hero-preview">
      <motion.div className="card p-6" initial={{ opacity: 0, y: 24, rotate: -1.5 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ duration: 0.8, delay: 0.2, ease: EASE }}>
        <div className="flex items-center justify-between"><p className="font-display font-semibold">Your IP action plan</p><span className="caption">Example preview</span></div>
        <ul className="mt-4 space-y-3">
          {PREVIEW_ROUTES.map(([r, s, c], i) => (
            <motion.li key={r} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.5 + i * 0.15, ease: EASE }}>
              <span className="flex items-center gap-2 text-sm font-medium"><ShieldCheck className="h-4 w-4 text-[#1F8A8B]" />{r}</span>
              {shown > i + 1 ? (
                <motion.span initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 400, damping: 18 }} className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${c}`} data-testid={`hero-route-${i}`}>{s}</motion.span>
              ) : <span className="flex items-center gap-1 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" />Checking</span>}
            </motion.li>
          ))}
        </ul>
        <div className="mt-5">
          <div className="flex justify-between text-xs text-slate-500"><span>Application progress</span><span data-testid="hero-progress-label">{steps} of 5 stages</span></div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100"><motion.div className="h-2 rounded-full bg-gradient-to-r from-[#56BEBF] to-[#1F8A8B]" initial={{ width: 0 }} animate={{ width: `${steps * 20}%` }} transition={{ duration: 0.6, ease: EASE }} /></div>
        </div>
      </motion.div>
      <motion.div className="card absolute -bottom-24 -left-8 hidden w-64 p-4 sm:block" initial={{ opacity: 0, y: 30, scale: 0.95 }} animate={still ? { opacity: 1, y: 0, scale: 1 } : { opacity: 1, y: [0, -8, 0], scale: 1 }}
        transition={still ? { duration: 0 } : { opacity: { delay: 2.4, duration: 0.5 }, scale: { delay: 2.4, duration: 0.5 }, y: { delay: 2.9, duration: 6, repeat: Infinity, ease: "easeInOut" } }} data-testid="hero-match-card">
        <span className="rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-800">Potential match</span>
        <p className="mt-2 text-sm font-semibold">Innovation feasibility grant</p>
        <p className="text-xs text-slate-500">Up to £50,000 per applicant · 70% of eligible costs</p>
      </motion.div>
    </div>
  );
};

const Orbs = () => {
  const still = useReducedMotion();
  const orb = (cls, x, y, d) => <motion.div aria-hidden className={`pointer-events-none absolute rounded-full blur-3xl ${cls}`} animate={still ? {} : { x: [0, x, 0], y: [0, y, 0] }} transition={{ duration: d, repeat: Infinity, ease: "easeInOut" }} />;
  return <>{orb("-top-24 right-[-6rem] h-80 w-80 bg-[#56BEBF]/25", -40, 30, 14)}{orb("bottom-[-8rem] left-[-4rem] h-96 w-96 bg-[#C8102E]/10", 50, -30, 18)}</>;
};

const Hero = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const go = (path) => (user ? navigate(path) : startLogin());
  return (
    <section className="soft-bg relative overflow-hidden">
      <Orbs />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 pb-24 pt-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pb-32 lg:pt-24">
        <div className="min-w-0 lg:col-span-7">
          <motion.p {...rise(0)} className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white px-3.5 py-1 text-xs font-medium text-[#1F8A8B]"><span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#56BEBF] opacity-75" /><span className="relative h-1.5 w-1.5 rounded-full bg-[#56BEBF]" /></span>IP protection and funding, made simple</motion.p>
          <motion.h1 {...rise(0.1)} data-testid="hero-headline" className="mt-6 text-4xl font-extrabold leading-[1.08] text-[#1E2A33] sm:text-5xl lg:text-6xl">Turn your innovation into a plan for <span className="hl">protection</span> and <span className="hl">funding</span>.</motion.h1>
          <motion.p {...rise(0.25)} className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">Describe your business once. Explore your protection options. Find funding. Prepare your applications.</motion.p>
          <motion.div {...rise(0.4)} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <button data-testid="hero-assess-ip-button" onClick={() => go("/app/projects")} className="btn-teal group px-7 py-3.5 text-base">Assess my IP <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></button>
            <button data-testid="hero-find-funding-button" onClick={() => navigate(user ? "/app/funding" : "/grants-funding")} className="btn-ghost px-7 py-3.5 text-base">Find funding</button>
          </motion.div>
          <motion.div {...rise(0.5)}><Link to="/what-you-need" data-testid="hero-what-you-need-link" className="group mt-5 inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-900 transition-colors hover:bg-teal-100"><ListChecks className="h-4 w-4" />See what you'll need to apply <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></motion.div>
          <motion.div {...rise(0.6)} className="mt-10"><PartnerMark /></motion.div>
          <motion.p {...rise(0.7)} className="mt-6 text-xs text-slate-500">Not a law firm or government service. No guaranteed outcomes.</motion.p>
        </div>
        <div className="min-w-0 lg:col-span-5">
          <Preview />
        </div>
      </div>
    </section>
  );
};

const PILLARS = [
  [ShieldCheck, "IP protection routes", "Patents, trade marks, registered designs, copyright records and confidential know-how, explained in plain English."],
  [Landmark, "Funding discovery", "Grants, loans, investment and procurement contracts, kept distinct, with individual award limits separated from programme budgets."],
  [FileCheck2, "Application preparation", "Drafts built from your own information, with every factual statement linked to its evidence."],
  [Lock, "Confidential by default", "Encrypted documents, isolated workspaces, optional MFA and activity logs. Your inventions are not used to train models."],
];

const Honesty = () => (
  <section className="bg-[#F2FAFA]">
    <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-8">
      <div>
        <p className="caption">What we will not do</p>
        <h2 className="mt-3 text-2xl font-bold text-[#0B192C] sm:text-3xl">Honest by design</h2>
      </div>
      <ul className="space-y-4 text-sm text-slate-700">
        {["We do not call a business idea patentable or guarantee any registration.", "We do not sell 'UK copyright registration' - it does not exist. Private records are labelled as such.",
          "We do not invent approval probabilities or guarantee funding.", "We never mark anything Submitted without an official acknowledgement or verified evidence.",
          "Preliminary searches are not clearance, patentability or freedom-to-operate opinions."].map((t) => (
          <li key={t} className="flex gap-3"><Ban className="mt-0.5 h-4 w-4 shrink-0 text-[#0D9488]" aria-hidden />{t}</li>
        ))}
      </ul>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map(([Icon, t, d], i) => (
            <div key={t} className="fade-up bg-white p-8" style={{ animationDelay: `${i * 0.06}s` }}>
              <Icon className="h-6 w-6 text-[#0D9488]" aria-hidden />
              <h3 className="mt-5 text-lg font-semibold text-[#0B192C]">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between">
          <h2 className="text-2xl font-bold text-[#0B192C] sm:text-3xl">The guided journey</h2>
          <Link to="/how-it-works" data-testid="home-how-it-works-link" className="hidden text-sm font-semibold text-[#0D9488] hover:underline sm:inline">How it works</Link>
        </div>
        <ol className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(([t, d], i) => (
            <li key={t} className="border-t-2 border-[#0B192C] pt-4">
              <span className="caption text-[#0D9488]">Step {i + 1}</span>
              <p className="mt-2 font-display font-semibold text-[#0B192C]">{t}</p>
              <p className="mt-1 text-sm text-slate-600">{d}</p>
            </li>
          ))}
          <li className="flex items-center"><ListChecks className="mr-2 h-5 w-5 text-[#0D9488]" /><span className="text-sm text-slate-600">Starting with the UK. Built so other countries can be added.</span></li>
        </ol>
      </section>
      <Honesty />
    </>
  );
}
